exports.isHTMLElement = (value) => typeof HTMLElement !== 'undefined' && value instanceof HTMLElement;

exports.resolveElement = (value) => {

    if (!value) {
        return null;
    }

    return exports.isHTMLElement(value)
        || typeof value.getBoundingClientRect === 'function'
        || typeof value.scrollIntoView === 'function'
        || typeof value.focus === 'function'
        ? value
        : exports.resolveElement(value.current || value.inputRef?.current || value.campusHousingTextfield?.current);
};

exports.escapeSelectorValue = (value) => {

    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(String(value));
    }

    return String(value).replace(/"/g, '\\"');
};

exports.getFieldSelectorValues = (field) => {

    if (!field) {
        return [];
    }

    const fieldName = String(field);
    const bracketPath = fieldName.replace(/\.([0-9]+)(?=\.|$)/g, '[$1]');
    const dotPath = fieldName.replace(/\[([0-9]+)\]/g, '.$1');

    return Array.from(new Set([
        fieldName,
        bracketPath,
        dotPath
    ]));
};

exports.queryFieldElement = ({
    field,
    formEl
}) => {

    if (!formEl || typeof formEl.querySelector !== 'function' || !field) {
        return null;
    }

    const selectorValues = exports.getFieldSelectorValues(field);

    for (const selectorValue of selectorValues) {
        const escapedField = exports.escapeSelectorValue(selectorValue);
        const fieldEl = formEl.querySelector(`[data-form-field-name="${escapedField}"]`)
            || formEl.querySelector(`[data-userinputs-field="${escapedField}"]`)
            || formEl.querySelector(`[data-search-field="${escapedField}"]`)
            || formEl.querySelector(`[name="${escapedField}"]`)
            || formEl.querySelector(`#${escapedField}-input`)
            || formEl.querySelector(`#${escapedField}-label`);

        if (fieldEl) {
            return fieldEl;
        }
    }

    return null;
};

exports.getFirstInvalidField = ({ fields = [], validate }) => {

    return fields.filter(validate).shift();
};

exports.getTopmostElement = (elements = []) => {
    const validElements = elements.map(exports.resolveElement).filter(Boolean);

    if (!validElements.length) {
        return null;
    }

    return validElements.reduce((topmost, candidate) => {

        if (!topmost) {
            return candidate;
        }

        const candidateTop = candidate.getBoundingClientRect?.().top ?? Number.POSITIVE_INFINITY;
        const topmostTop = topmost.getBoundingClientRect?.().top ?? Number.POSITIVE_INFINITY;

        return candidateTop < topmostTop ? candidate : topmost;
    }, null);
};

exports.getInvalidFieldElement = ({
    field,
    formEl,
    refs = {},
    specifyGenderTextfield
}) => {

    if (field === 'gender' && specifyGenderTextfield?.current) {
        return exports.resolveElement(specifyGenderTextfield.current);
    }

    const refElement = exports.resolveElement(
        refs[field]?.current
            || refs[field]
    );

    if (refElement) {
        return refElement;
    }

    return exports.queryFieldElement({
        field,
        formEl
    });
};

exports.getScrollTargetElement = (fieldEl) => {

    const resolved = exports.resolveElement(fieldEl);

    if (!resolved) {
        return null;
    }

    if (typeof resolved.closest !== 'function') {
        return resolved;
    }

    return resolved.closest([
        '[data-form-field-root]',
        '[data-form-field-name]',
        '[data-userinputs-field]',
        '.MuiFormControl-root',
        '.MuiAutocomplete-root',
        '.housingPicker',
        '[role="radiogroup"]'
    ].join(','))
        || resolved;
};

exports.getFocusableElement = (fieldEl) => {

    const resolved = exports.resolveElement(fieldEl);

    if (!resolved) {
        return null;
    }

    if (!exports.isHTMLElement(resolved)) {
        return resolved;
    }

    if (resolved.matches?.('[aria-invalid="true"], input, textarea, select, button, [tabindex]')) {
        return resolved;
    }

    return resolved.querySelector('[aria-invalid="true"], input, textarea, select, button, [tabindex]')
        || resolved;
};

exports.getPreserveErrorOnFocusElement = (target) => {

    const resolved = exports.resolveElement(target);

    if (!resolved || typeof resolved.closest !== 'function') {
        return null;
    }

    return resolved.closest('[data-preserve-error-on-focus="true"]');
};

exports.shouldPreserveErrorOnFocus = (eventOrElement) => {

    if (!eventOrElement) {
        return false;
    }

    const target = eventOrElement.target || eventOrElement.currentTarget || eventOrElement;

    return !!exports.getPreserveErrorOnFocusElement(target);
};

exports.withPreserveErrorOnFocus = (elements = [], callback) => {

    const taggedElements = elements
        .map(exports.resolveElement)
        .filter((element) => exports.isHTMLElement(element));

    taggedElements.forEach((element) => {
        element.dataset.preserveErrorOnFocus = 'true';
    });

    try {
        return callback?.();
    }
    finally {
        const clear = () => taggedElements.forEach((element) => {
            delete element.dataset.preserveErrorOnFocus;
        });

        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
            window.setTimeout(clear, 0);
            return;
        }

        clear();
    }
};

exports.getScrollPadding = () => {

    const scrollContainer = exports.getAppScrollContainer();
    const viewportHeight = scrollContainer?.clientHeight
        || (typeof window !== 'undefined' ? window.innerHeight : 0);

    if (!viewportHeight) {
        return 120;
    }

    return Math.max(96, Math.min(180, Math.round(viewportHeight * 0.18)));
};

exports.getAppScrollContainer = () => {

    if (typeof document === 'undefined') {
        return null;
    }

    return document.getElementById('scrollContainer');
};

exports.isScrollableElement = (el) => {

    if (!el || el === document.body || el === document.documentElement) {
        return false;
    }

    const style = typeof window !== 'undefined' && typeof window.getComputedStyle === 'function'
        ? window.getComputedStyle(el)
        : null;
    const overflowY = style?.overflowY || '';
    const canScroll = /(auto|scroll|overlay)/.test(overflowY);

    return canScroll && el.scrollHeight > el.clientHeight;
};

exports.getScrollContainer = (fieldEl) => {

    const appScrollContainer = exports.getAppScrollContainer();

    if (appScrollContainer?.contains?.(fieldEl)) {
        return appScrollContainer;
    }

    let parent = fieldEl?.parentElement;

    while (parent) {
        if (exports.isScrollableElement(parent)) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return document.scrollingElement
        || document.documentElement
        || document.body;
};

exports.scrollFieldElementIntoView = (fieldEl) => {

    const targetEl = exports.getScrollTargetElement(fieldEl);

    if (!targetEl) {
        return;
    }

    const rect = targetEl.getBoundingClientRect?.();

    if (!rect || typeof document === 'undefined') {
        targetEl.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        return;
    }

    const padding = exports.getScrollPadding();
    const scrollContainer = exports.getScrollContainer(targetEl);

    if (!scrollContainer || scrollContainer === document.documentElement || scrollContainer === document.body) {
        const scrollTop = window.pageYOffset
            || document.documentElement?.scrollTop
            || document.body?.scrollTop
            || 0;
        const targetTop = Math.max(0, scrollTop + rect.top - padding);

        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
            window.scrollTo({
                top: targetTop,
                behavior: 'smooth'
            });
            return;
        }

        targetEl.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetTop = Math.max(
        0,
        scrollContainer.scrollTop + rect.top - containerRect.top - padding
    );

    scrollContainer.scrollTo({
        top: targetTop,
        behavior: 'smooth'
    });
};

exports.focusElementWithoutScrolling = (fieldEl) => {

    if (typeof fieldEl?.focus !== 'function') {
        return;
    }

    try {
        fieldEl.focus({ preventScroll: true });
    }
    catch (err) {
        fieldEl.focus();
    }
};

exports.focusInvalidFieldElement = (fieldEl) => {

    const resolved = exports.resolveElement(fieldEl);

    if (!resolved) {
        return;
    }

    exports.scrollFieldElementIntoView(resolved);

    if (!exports.isHTMLElement(resolved) && typeof resolved.scrollIntoView === 'function') {
        return;
    }

    const focusableElement = exports.getFocusableElement(resolved);
    const scrollTargetElement = exports.getScrollTargetElement(resolved);

    exports.withPreserveErrorOnFocus([
        resolved,
        scrollTargetElement,
        focusableElement
    ], () => exports.focusElementWithoutScrolling(focusableElement));
};

exports.focusFirstInvalidField = ({
    fields = [],
    formEl,
    validate,
    refs = {},
    specifyGenderTextfield
}) => {

    const invalidFields = fields.filter(validate);
    const firstInvalidField = invalidFields[0];
    const invalidFieldElements = invalidFields.map((field) => exports.getInvalidFieldElement({
        field,
        formEl,
        refs,
        specifyGenderTextfield
    }));
    const root = formEl || (typeof document !== 'undefined' ? document : null);
    const topmostInvalidFieldEl = exports.getTopmostElement(invalidFieldElements)
        || exports.getTopmostElement(Array.from(root?.querySelectorAll?.('[aria-invalid="true"]') || []));
    const fieldEl = topmostInvalidFieldEl || exports.getInvalidFieldElement({
        field: firstInvalidField,
        formEl,
        refs,
        specifyGenderTextfield
    });

    exports.focusInvalidFieldElement(fieldEl);

    return firstInvalidField;
};

exports.scheduleFocusFirstInvalidField = (options = {}) => {

    const schedule = options.scheduler || ((callback) => {

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            return window.requestAnimationFrame(callback);
        }

        return setTimeout(callback, 0);
    });

    return schedule(() => exports.focusFirstInvalidField({
        ...options,
        scheduler: undefined
    }));
};

exports.normalizeSubmitErrors = (errors = []) => [].concat(errors || [])
    .map((error) => {

        if (!error) {
            return null;
        }

        if (typeof error === 'string') {
            return {
                field: error,
                message: 'This field is invalid'
            };
        }

        return error;
    })
    .filter((error) => error?.field);

exports.getFormElement = ({
    formEl,
    formId
} = {}) => {

    if (formEl) {
        return formEl;
    }

    if (!formId || typeof document === 'undefined') {
        return null;
    }

    return document.getElementById(formId);
};

exports.applySubmitErrors = ({
    clearExisting = true,
    errors = [],
    formEl,
    formId,
    methods = {},
    refs = {},
    scheduler,
    specifyGenderTextfield
} = {}) => {

    const normalizedErrors = exports.normalizeSubmitErrors(errors);

    if (!normalizedErrors.length) {
        return false;
    }

    if (clearExisting) {
        methods.clearErrors?.();
    }

    normalizedErrors.forEach(({
        field,
        message,
        type = 'manual'
    }) => {
        methods.setError?.(field, {
            type,
            message
        });
    });

    const invalidFields = normalizedErrors.map(({ field }) => field);
    const invalidFieldSet = new Set(invalidFields.flatMap(exports.getFieldSelectorValues));

    exports.scheduleFocusFirstInvalidField({
        fields: invalidFields,
        formEl: exports.getFormElement({
            formEl,
            formId
        }),
        refs,
        scheduler,
        specifyGenderTextfield,
        validate: (field) => exports.getFieldSelectorValues(field)
            .some((selectorValue) => invalidFieldSet.has(selectorValue))
    });

    return true;
};
